import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CardActionArea from "@mui/material/CardActionArea";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import Popper, { type PopperPlacementType } from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { MarketplaceResultType } from "config";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Link as RouterLink } from "react-router";
import { getMarketplaceProfilePath } from "marketplace/_views/marketplace-profile-link";
import Individual from "marketplace/individuals/individual";
import IndividualBusinessCard from "marketplace/individuals/profiles/_views/individual-business-card";

/** Long enough that a pointer passing over a column of names does not flash a card on each. */
export const individualCardOpenDelayMs = 300;
/** Short enough to feel immediate, long enough that moving to the next name keeps a card up. */
export const individualCardCloseDelayMs = 100;

/**
 * Over a dialog as well as over a page: the submit confirmation is a dialog, and a card under it
 * would be no card at all.
 *
 * Only a card with something on it to act on takes pointer events. Every other card — a peek, or a
 * pinned card that is not a link — lets a click through to whatever is under it, so wherever the card
 * lands it can never stand between a reader and a control. That click is outside the card, so it also
 * puts the card down.
 */
const CardPopper = styled(Popper, {
  shouldForwardProp: (prop) => prop !== "interactive",
})<{ interactive: boolean }>(({ theme, interactive }) => ({
  pointerEvents: interactive ? "auto" : "none",
  zIndex: theme.zIndex.tooltip,
}));
/** The whole card, as one link to the profile. A block, so the card inside keeps its own layout. */
const CardLink = styled(CardActionArea)(() => ({
  display: "block",
})) as typeof CardActionArea;
const Footer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(0.5),
  justifyContent: "flex-end",
  "& svg": { fontSize: 16 },
}));

type CardState = "closed" | "peek" | "pinned";

export type IndividualCardTriggerProps = {
  onMouseEnter: (event: MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
  onFocus: (event: FocusEvent<HTMLElement>) => void;
  onBlur: () => void;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  "aria-haspopup": "dialog" | undefined;
  "aria-expanded": boolean;
  "aria-describedby": string | undefined;
};

export type IndividualCardTrigger = {
  /** Spread onto the name that opens the card. It has to be a button, so a click and a key reach it. */
  triggerProps: IndividualCardTriggerProps;
  /** The card itself. Render it anywhere beside the trigger; it is portalled to the page. */
  card: ReactNode;
};

/**
 * A person's business card, opened from their name (#5813).
 *
 * Pointing at the name peeks at the card after {@link individualCardOpenDelayMs}, and moving away puts
 * it down again. Keyboard focus peeks at once. A click, or Enter, pins the card: it stays up when the
 * pointer leaves. The name again, Escape, or a click anywhere outside the card puts it down, pinned or
 * peeking, so a card never outlives the reader's attention moving to something else.
 *
 * What a pinned card is depends on whether there is anything on it to act on.
 *
 * - **A link to the profile.** It is a dialog. Focus moves to the link as it opens, so a keyboard
 *   reader can follow it: the card is portalled to the end of the page, and Tab from the name would
 *   never reach it. Escape and Tab both put it down and hand focus back to the name, so the reader
 *   carries on from where they opened it.
 * - **Nothing to act on** — `linksToProfile: false`, or a person known only as a contact. It is a
 *   toggled tooltip. Focus stays on the name, which is described by the card while it is up, and a
 *   click passes through the card to whatever is under it (see `CardPopper`).
 *
 * `linksToProfile: false` keeps a pinned card off the profile, for a surface where leaving would drop
 * work in flight, such as the submit confirmation.
 *
 * `placement` is where the card opens beside the name, below it by default, and `fallbackPlacements`
 * where it goes when the window has no room there.
 */
export default function useIndividualCardTrigger(
  individual: Individual | undefined,
  options: {
    linksToProfile?: boolean;
    placement?: PopperPlacementType;
    fallbackPlacements?: readonly PopperPlacementType[];
  } = {},
): IndividualCardTrigger {
  const linksToProfile = options.linksToProfile ?? true;
  const placement = options.placement ?? "bottom-start";
  const fallbackPlacements = options.fallbackPlacements ?? ["top-start"];
  const [state, setState] = useState<CardState>("closed");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  /** Set by the click that pins a card with a link on it, and spent by the one focus that follows. */
  const linkOwesFocusRef = useRef(false);
  const cardId = useId();
  const titleId = useId();

  const pinned = state === "pinned";
  const hasProfile = linksToProfile && !!individual && !individual.contactOnly;
  const interactive = pinned && hasProfile;

  const clearTimer = useCallback(() => {
    if (timer.current === undefined) return;
    window.clearTimeout(timer.current);
    timer.current = undefined;
  }, []);
  /**
   * Puts the card down. Focus inside it goes back to the name, so a keyboard reader is never left on a
   * card that has gone; focus anywhere else stays where the reader put it.
   */
  const close = useCallback(() => {
    clearTimer();
    if (anchorEl && surfaceRef.current?.contains(document.activeElement)) anchorEl.focus();
    setState("closed");
  }, [anchorEl, clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);
  useEffect(() => {
    if (state === "closed") return;
    // Captured and stopped, so the first Escape closes only the card and not a dialog around it.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      close();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [close, state]);

  /**
   * The link takes focus as the card it sits on is pinned; see the docblock for why.
   *
   * Once per pin, not on every call: MUI hands the link a new merged ref on each render, so React calls
   * this again whenever the card re-renders, and focusing each time pulled focus back off whatever the
   * reader had moved on to — a field they had just clicked into, say.
   */
  const focusOnMount = useCallback((link: HTMLElement | null) => {
    if (!link || !linkOwesFocusRef.current) return;
    linkOwesFocusRef.current = false;
    link.focus();
  }, []);

  const triggerProps: IndividualCardTriggerProps = {
    onMouseEnter: (event) => {
      if (!individual || pinned) return;
      const target = event.currentTarget;
      clearTimer();
      timer.current = window.setTimeout(() => {
        setAnchorEl(target);
        setState("peek");
      }, individualCardOpenDelayMs);
    },
    onMouseLeave: () => {
      clearTimer();
      if (state === "peek") timer.current = window.setTimeout(close, individualCardCloseDelayMs);
    },
    onFocus: (event) => {
      // A click focuses the name too, and a click pins; only keyboard focus peeks.
      if (!individual || state !== "closed" || !isKeyboardFocused(event.currentTarget)) return;
      setAnchorEl(event.currentTarget);
      setState("peek");
    },
    onBlur: () => {
      if (state === "peek") close();
    },
    onClick: (event) => {
      if (!individual) return;
      clearTimer();
      setAnchorEl(event.currentTarget);
      linkOwesFocusRef.current = !pinned && hasProfile;
      setState(pinned ? "closed" : "pinned");
    },
    "aria-haspopup": hasProfile ? "dialog" : undefined,
    "aria-expanded": pinned,
    // While the card has nothing to focus, it describes the name instead.
    "aria-describedby": state !== "closed" && !interactive ? cardId : undefined,
  };

  /** Tab out of a pinned card goes back to the name, not to the end of the page the card is portalled to. */
  function handleSurfaceKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Tab") return;
    event.preventDefault();
    close();
  }

  let face: ReactNode = null;
  if (individual) {
    face = (
      <IndividualBusinessCard
        individual={individual}
        titleId={titleId}
        footer={pinned && linksToProfile ? <ProfileFooter hasProfile={hasProfile} /> : undefined}
      />
    );
  }

  const card =
    individual && anchorEl ? (
      <CardPopper
        id={cardId}
        open={state !== "closed"}
        anchorEl={anchorEl}
        interactive={interactive}
        placement={placement}
        transition
        // A card with a link on it is a dialog the reader opened; any other stays the tooltip a Popper is.
        role={interactive ? "dialog" : "tooltip"}
        aria-labelledby={interactive ? titleId : undefined}
        modifiers={[
          { name: "offset", options: { offset: [0, 6] } },
          { name: "preventOverflow", options: { padding: 8 } },
          { name: "flip", options: { fallbackPlacements } },
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={120}>
            <div>
              <ClickAwayListener
                onClickAway={(event) => {
                  // The name's own click toggles the card; letting this close it first would reopen it.
                  if (!anchorEl.contains(event.target as Node)) close();
                }}
              >
                <Paper elevation={8} ref={surfaceRef} onKeyDown={interactive ? handleSurfaceKeyDown : undefined}>
                  {interactive ? (
                    <CardLink
                      ref={focusOnMount}
                      component={RouterLink}
                      to={getMarketplaceProfilePath(MarketplaceResultType.Individual, individual.id.value)}
                      aria-label={`View ${individual.fullName}'s profile`}
                      onClick={close}
                    >
                      {face}
                    </CardLink>
                  ) : (
                    face
                  )}
                </Paper>
              </ClickAwayListener>
            </div>
          </Fade>
        )}
      </CardPopper>
    ) : null;

  return { triggerProps, card };
}

function ProfileFooter({ hasProfile }: Readonly<{ hasProfile: boolean }>) {
  return (
    <Footer>
      {hasProfile ? (
        <>
          <Typography variant="caption" color="primary">View profile</Typography>
          <ArrowForwardIcon color="primary" aria-hidden="true" />
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">Profile not published</Typography>
      )}
    </Footer>
  );
}

function isKeyboardFocused(element: HTMLElement): boolean {
  try {
    return element.matches(":focus-visible");
  } catch {
    // An engine without :focus-visible cannot tell a click from a Tab; showing the card is the safer mistake.
    return true;
  }
}
